import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type BetaToggleService from 'scaleflow/services/beta-toggle';

type Tab = { label: string; route: string; testId: string };

/**
 * The Active / Archived toggle above the list. Mirrors
 * `app/controllers/settings/teams.js` — same tab pill styling, same
 * `isActive` comparison — so the two pages read identically.
 */
export default class ScansListController extends Controller {
  @service declare router: RouterService;
  @service declare betaToggle: BetaToggleService;

  get tabs(): Tab[] {
    return [
      {
        label: 'dictionary.active',
        route: 'scans.list.active',
        testId: 'scans-tab-active',
      },
      {
        label: 'dictionary.archived',
        route: 'scans.list.archived',
        testId: 'scans-tab-archived',
      },
    ];
  }

  /**
   * Archiving is an operator concern, so only platform admins get the toggle.
   * `scans.list.archived` guards the URL to match — hiding the control alone
   * would leave the tab reachable by hand.
   */
  get showTabs(): boolean {
    return this.betaToggle.isPlatformAdmin;
  }

  /**
   * `currentRoute.name` resolves to the leaf, which is `…active.index` for a
   * route with no child of its own. Drop the trailing `index` so it compares
   * against the tab's own route name.
   */
  @action
  extractRouteName(name: string): string {
    if (!name.includes('index')) return name;

    const routeParts = name.split('.');
    routeParts.splice(routeParts.length - 1, 1);
    return routeParts.join('.');
  }

  @action
  isActive(tab: Tab): boolean {
    const currentRouteName = this.extractRouteName(
      this.router.currentRoute?.name ?? '',
    );
    return currentRouteName === this.extractRouteName(tab.route);
  }
}
